import { useEffect, useRef } from 'react';
import { topicOf, type LiveTopic } from '@forecastify/shared';
import { useLive } from './live-provider';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C21 · "something I read has changed — refetch." R-19.                     │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The whole client half of live updates, from a screen's point of view: name the topics you read
 * and hand over the `reload` you already have. Every route already exposes one, which is why this
 * needed no rewrite of how screens fetch.
 *
 * ⚠️⚠️ **THE REFETCH IS SILENT AND IN PLACE.** None of the routes clears its state before
 * refetching — they fetch, then swap — so the screen never blanks and the page never reloads. That
 * is not luck; it is the property `state-rules.md` now requires, and a spinner that appears
 * because a colleague saved something is more disruptive than the stale row it replaces.
 */

/**
 * ⚠️ **A burst is ONE refetch.** An import, a bulk status change or a parent task moving with its
 * descendants writes an audit row per row touched — thirty events in a few hundred milliseconds.
 * Without this, thirty refetches and thirty renders. The window is short enough that a person
 * cannot perceive it and long enough to swallow any single user action's fan-out.
 */
const COALESCE_MS = 250;

export interface LiveOptions {
  /**
   * The member whose own changes should not cause a refetch — normally the caller.
   *
   * ⚠️ **Optional, and OFF by default, which is the safe direction.** Ignoring your own events
   * saves a round trip after every save; getting it wrong means a screen that never updates for
   * the one person most likely to notice. A screen opts in only where it already applies the
   * change locally.
   */
  readonly ignoreActor?: string | null;
  /**
   * 30 Oct 2026 · **the least time between two refetches, while events keep coming.** A Jira sync
   * applies issue after issue, each one a `task` event, and the 250 ms coalescing still refetched
   * about once a second for as long as it ran — the contract screen's line item pulsed and its
   * rule chip flickered (UAT). With this, a stream refetches at most every `steadyMs`, and the
   * last event is still followed by a refetch: nothing is missed, it is only not chased.
   */
  readonly steadyMs?: number;
}

/**
 * Refetch when anything in `topics` changes.
 *
 * @param topics One of the named sets in `topics.ts`. ⚠️ Pass a module-level constant, not an
 * inline array: a new array every render would re-subscribe every render.
 */
export function useLiveInvalidation(
  topics: readonly LiveTopic[],
  reload: () => void | Promise<void>,
  options: LiveOptions = {},
): void {
  const live = useLive();

  /**
   * ⚠️ **`reload` in a ref, and NOT in the dependency array.** Every route builds it with
   * `useCallback`, and several of those depend on `session` — which is a fresh object every
   * render. Listing it here would resubscribe on every render of the tree, which for a set of
   * listeners is merely wasteful and for the stream underneath would be fatal.
   */
  const reloadRef = useRef(reload);
  reloadRef.current = reload;

  const ignoreActor = options.ignoreActor ?? null;
  const steadyMs = options.steadyMs ?? 0;

  /* ⚠️ Joined into a string so the effect below can depend on the CONTENTS rather than on the
   * array's identity. The sets are module constants today, and this stops that being load-bearing
   * the day somebody builds one from props. */
  const key = topics.join(',');

  useEffect(() => {
    if (live === null) return;

    const wanted = new Set(key.split(','));
    let timer: ReturnType<typeof setTimeout> | null = null;
    /* When the last refetch ran — `steadyMs` keeps the next one at least that far after it. */
    let lastRun = 0;

    const schedule = (): void => {
      if (timer !== null) return;
      const delay = Math.max(COALESCE_MS, lastRun + steadyMs - Date.now());
      timer = setTimeout(() => {
        timer = null;
        lastRun = Date.now();
        void reloadRef.current();
      }, delay);
    };

    const unsubscribe = live.subscribe((event) => {
      /**
       * ⚠️ **`null` is a RESYNC, and it always refetches.** The stream reconnected and has no idea
       * what it missed — `hard-won.md` P1-T18-3 settled that a resume is a refetch rather than a
       * replay. Filtering it by topic would be filtering on information nobody has.
       */
      if (event === null) {
        schedule();
        return;
      }

      if (!wanted.has(topicOf(event.action))) return;

      /* The echo of this tab's own write, when the caller asked to ignore it. */
      if (ignoreActor !== null && event.actorMemberId === ignoreActor) return;

      schedule();
    });

    return () => {
      unsubscribe();
      if (timer !== null) clearTimeout(timer);
    };
  }, [live, key, ignoreActor, steadyMs]);
}
